import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'
import * as adminApi from '../../api/admin'
import type { CrowdProduct } from '../../types'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  crowd_proposed: { bg: '#f59e0b22', color: '#f59e0b' },
  published:      { bg: '#22c55e22', color: '#22c55e' },
  archived:       { bg: '#6b728022', color: '#9ca3af' },
  rejected:       { bg: '#ef444422', color: '#ef4444' },
}

const STATUS_LABELS: Record<string, string> = {
  crowd_proposed: 'Needs Review',
  published:      'Approved',
  archived:       'Rejected',
  draft:          'Draft',
}

export default function AdminProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const { apiKey } = useAdminStore()

  const [product, setProduct] = useState<CrowdProduct | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!productId) return
    // Load product from the list endpoint filtered by id via a direct fetch
    const fetchProduct = async () => {
      setLoading(true)
      try {
        // Since there's no single-product endpoint, fetch a broad list to find by ID
        const all = await adminApi.listCrowdProducts({ skip: 0, limit: 200 })
        const found = all.items.find((p) => p.id === productId) ?? null
        setProduct(found)
        if (!found) setError('Product not found')
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    }
    fetchProduct()
  }, [productId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePublish = async () => {
    if (!productId) return
    setBusy(true)
    try {
      await adminApi.publishProduct(productId)
      setProduct((p) => p ? { ...p, status: 'published' } : p)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed')
    } finally {
      setBusy(false)
    }
  }

  const handleArchive = async () => {
    if (!productId) return
    setBusy(true)
    try {
      await adminApi.archiveProduct(productId)
      setProduct((p) => p ? { ...p, status: 'archived' } : p)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed')
    } finally {
      setBusy(false)
    }
  }

  if (!apiKey) return null

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="p-4 text-center">
        <p style={{ color: '#ef4444' }}>{error ?? 'Product not found'}</p>
        <button
          className="mt-4 text-sm"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    )
  }

  const s = STATUS_COLORS[product.status ?? ''] ?? { bg: '#6b728022', color: '#9ca3af' }

  return (
    <div className="p-4 space-y-4 pb-8">
      <button
        className="text-sm"
        style={{ color: 'var(--tg-theme-link-color)' }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      {/* Thumbnail */}
      {product.thumbnail_url && (
        <div className="rounded-2xl overflow-hidden aspect-square w-full max-w-xs mx-auto">
          <img
            src={product.thumbnail_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Status badge */}
      <div className="flex items-center gap-2">
        <span
          className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ background: s.bg, color: s.color }}
        >
          {STATUS_LABELS[product.status ?? ''] ?? product.status ?? 'Unknown'}
        </span>
        {product.translations && Object.keys(product.translations).length > 0 && (
          <span
            className="text-xs px-1.5 py-0.5 rounded font-mono uppercase"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-text-color)', opacity: 0.6 }}
          >
            {Object.keys(product.translations)[0]}
          </span>
        )}
        {product.price && (
          <span className="text-base font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
            {product.price.toLocaleString()} {product.price_currency ?? 'UZS'}
          </span>
        )}
      </div>

      {/* Name */}
      <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
        {product.name}
      </h1>

      {/* Description */}
      {product.description && (
        <p className="text-sm opacity-70 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
          {product.description}
        </p>
      )}

      {/* Keywords */}
      {product.keywords && product.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {product.keywords.map((kw, i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded text-xs"
              style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            >
              {kw}
            </span>
          ))}
        </div>
      )}

      {/* Shop */}
      {product.merchant_name && (
        <div
          className="rounded-xl p-3 flex items-center gap-3"
          style={{ background: 'var(--app-card-bg)' }}
        >
          <span className="text-xl">🏪</span>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
              {product.merchant_name}
            </p>
            {product.merchant_id && (
              <button
                className="text-xs"
                style={{ color: 'var(--tg-theme-link-color)' }}
                onClick={() => navigate(`/admin/shops/${product.merchant_id}`)}
              >
                View shop →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Meta */}
      {product.created_at && (
        <p className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
          Added {new Date(product.created_at).toLocaleString()}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        {product.status !== 'published' && (
          <button
            className="flex-1 py-3 rounded-2xl font-semibold text-sm disabled:opacity-40"
            style={{ background: '#22c55e', color: 'white' }}
            disabled={busy}
            onClick={handlePublish}
          >
            {busy ? '…' : 'Approve'}
          </button>
        )}
        {product.status === 'published' && (
          <div
            className="flex-1 py-3 rounded-2xl text-center text-sm font-medium"
            style={{ background: '#22c55e22', color: '#22c55e' }}
          >
            ✓ Approved
          </div>
        )}
        {product.status !== 'archived' && (
          <button
            className="flex-1 py-3 rounded-2xl font-semibold text-sm disabled:opacity-40"
            style={{ background: '#ef444418', color: '#ef4444' }}
            disabled={busy}
            onClick={handleArchive}
          >
            Reject
          </button>
        )}
      </div>
    </div>
  )
}
